/**
 * LightController.jsx (User Portal)
 * Ported from botriq-admin-fe LightController — same functionality, same API endpoints.
 * Auth: uses portalToken (not admin token).
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  Typography,
  Avatar,
  Card,
  CardContent,
  Switch,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
} from "@mui/material";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SpeedIcon from "@mui/icons-material/Speed";
import CONFIG from "../../config/config";
import { apiFetch } from "../../services/http";
import { tokens } from "../../theme";

// ── Helpers ────────────────────────────────────────────────────────────────────

const postApi = async (url, payload, token) => {
  try {
    await apiFetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    console.error("[LightController] API error:", err);
  }
};

const debounce = (fn, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

const hexToRgbString = (hex) => {
  const n = Number.parseInt(hex.slice(1), 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
};

const EFFECTS = ["OFF", "ON", "SOLID", "RAINBOW", "THEATER", "WIPE", "PULSE", "BLINK"];

// ── Small sub-card ─────────────────────────────────────────────────────────────
const SubCard = ({ icon, title, disabled, children }) => (
  <Card
    elevation={0}
    sx={{
      border: `1px solid ${disabled ? tokens.border : tokens.accent}`,
      borderRadius: "12px",
      background: disabled ? tokens.surface2 : tokens.surface,
      opacity: disabled ? 0.6 : 1,
      transition: "opacity 0.3s",
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      sx={{
        px: 2,
        py: 1.25,
        background: tokens.surface2,
        borderBottom: `1px solid ${tokens.border}`,
      }}
    >
      <Box sx={{ color: tokens.accent }}>{icon}</Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: tokens.text }}>{title}</Typography>
    </Stack>
    <CardContent sx={{ p: 2 }}>{children}</CardContent>
  </Card>
);

SubCard.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.node,
  disabled: PropTypes.bool,
  children: PropTypes.node,
};

// Reads the currently-selected robot straight from localStorage (same source
// callApi already trusts), so the seed below is never stale relative to the
// serialID a command would target.
const readSelectedRobo = () => {
  try {
    return JSON.parse(localStorage.getItem("selectedRobo") || "null");
  } catch {
    return null;
  }
};

// Mirrors the applied config onto the cached robot so reopening this drawer
// (which re-seeds from readSelectedRobo()) reflects the last-set values.
const writeLightConfigToSelectedRobo = (cfg) => {
  const current = readSelectedRobo();
  if (!current) return;
  localStorage.setItem("selectedRobo", JSON.stringify({ ...current, lightConfig: cfg }));
};

// All on/off presentation for the toggle card in one place, computed once from
// `isOn` — the JSX below reads plain fields instead of repeating `isOn ? … : …`
// nine times, which is what pushed the component over the complexity budget.
const getToggleTheme = (isOn) => ({
  cardBorder: isOn ? tokens.warning : tokens.border,
  cardBg: isOn ? tokens.warningSoft : tokens.surface,
  avatarBg: isOn ? tokens.warningSoft : tokens.surface3,
  Icon: isOn ? LightbulbIcon : LightbulbOutlinedIcon,
  iconColor: isOn ? tokens.warning : tokens.text3,
  title: isOn ? "Light is ON" : "Light is OFF",
  hint: isOn ? "Click to turn off" : "Click to turn on",
  chipLabel: isOn ? "ACTIVE" : "INACTIVE",
  chipColor: isOn ? tokens.warning : tokens.text2,
  chipBg: isOn ? tokens.warningSoft : tokens.surface3,
});

// Colour-swatch presentation, split out for the same reason — depends on both
// isOn and the current colour, so it's a separate derivation from the toggle
// theme above.
const getSwatchTheme = (isOn, color) => ({
  border: isOn ? color : tokens.border,
  background: isOn ? `${color}1a` : tokens.surface2,
  cursor: isOn ? "pointer" : "not-allowed",
  opacity: isOn ? 1 : 0.5,
});

// ── Component ─────────────────────────────────────────────────────────────
// `robot` is optional: omit it to control `selectedRobo` (DeliveryLayout);
// pass it explicitly when the caller already knows the robot (Robot Details).
const LightController = ({ robot }) => {
  const token = localStorage.getItem("portalToken");
  const colorInputRef = useRef(null);
  // Seed from the robot's last-applied light config (persisted server-side).
  const seed = (robot ?? readSelectedRobo())?.lightConfig ?? {};
  const [isOn, setIsOn] = useState(Boolean(seed.isOn));
  const [color, setColor] = useState(seed.color || "#ff0000");
  const [effect, setEffect] = useState(seed.effect || "OFF");
  const [speed, setSpeed] = useState(seed.speed ?? 1000);

  const callApi = useCallback(() => {
    const serialID = robot?.serialID ?? readSelectedRobo()?.serialID ?? null;
    if (!serialID) return;
    if (isOn) {
      // `colorHex` is stored server-side so reopening seeds from the exact hex
      // the picker uses; `color` (RGB) is what the robot command consumes.
      postApi(
        `${CONFIG.BASE_URL}/api/adbToSocket/light-on`,
        { serialID, speed, effect, color: hexToRgbString(color), colorHex: color },
        token
      );
    } else {
      postApi(`${CONFIG.BASE_URL}/api/adbToSocket/light-off`, { serialID }, token);
    }
  }, [robot, isOn, color, effect, speed, token]);

  const debouncedCallApi = useMemo(() => debounce(callApi, 600), [callApi]);
  useEffect(() => {
    debouncedCallApi();
    // Only mirror onto `selectedRobo` when this dialog IS controlling that
    // robot (no explicit `robot` passed in) — an explicit robot from Robot
    // Details must not overwrite a different robot's cached light config.
    if (!robot) writeLightConfigToSelectedRobo({ isOn, color, effect, speed });
  }, [robot, isOn, color, effect, speed, debouncedCallApi]);

  const theme = getToggleTheme(isOn);
  const swatch = getSwatchTheme(isOn, color);

  return (
    <Box sx={{ p: 2, boxSizing: "border-box" }}>
      <Stack gap={2}>
        {/* ── Toggle card ─────────────────────────────────────────────────── */}
        <Card
          elevation={0}
          sx={{
            border: `1.5px solid ${theme.cardBorder}`,
            borderRadius: "12px",
            background: theme.cardBg,
            transition: "all 0.3s",
          }}
        >
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    background: theme.avatarBg,
                  }}
                >
                  <theme.Icon sx={{ fontSize: 18, color: theme.iconColor }} />
                </Avatar>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: tokens.text }}>
                    {theme.title}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: tokens.text2 }}>{theme.hint}</Typography>
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                <Chip
                  label={theme.chipLabel}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 10,
                    fontWeight: 700,
                    borderRadius: "20px",
                    color: theme.chipColor,
                    background: theme.chipBg,
                  }}
                />
                <Switch
                  checked={isOn}
                  onChange={() => setIsOn((p) => !p)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: tokens.warning },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: tokens.warning,
                    },
                  }}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* ── Color picker ────────────────────────────────────────────────── */}
        <SubCard
          icon={<ColorLensIcon sx={{ fontSize: 16 }} />}
          title="Choose Color"
          disabled={!isOn}
        >
          <input
            ref={colorInputRef}
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            disabled={!isOn}
            style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
          />
          <Box
            onClick={() => isOn && colorInputRef.current?.click()}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 1.5,
              borderRadius: "10px",
              border: `2px solid ${swatch.border}`,
              background: swatch.background,
              cursor: swatch.cursor,
              opacity: swatch.opacity,
              transition: "all 0.3s",
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: "10px",
                background: color,
                flexShrink: 0,
                border: "3px solid rgba(255,255,255,0.9)",
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: tokens.text }}>
                Selected Color
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: tokens.text2,
                  fontFamily: "monospace",
                  letterSpacing: 1,
                }}
              >
                {color.toUpperCase()}
              </Typography>
              <Typography sx={{ fontSize: 11, color: tokens.text3 }}>
                RGB: {hexToRgbString(color)}
              </Typography>
            </Box>
          </Box>
        </SubCard>

        {/* ── Effect selector ─────────────────────────────────────────────── */}
        <SubCard
          icon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
          title="Lighting Effect"
          disabled={!isOn}
        >
          <FormControl
            fullWidth
            size="small"
            disabled={!isOn}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "9px",
                fontSize: 13,
                background: tokens.surface,
                "& fieldset": { borderColor: tokens.border },
                "&:hover fieldset": { borderColor: tokens.borderStrong },
                "&.Mui-focused fieldset": { borderColor: tokens.accent, borderWidth: 1.5 },
              },
              "& .MuiInputLabel-root": { fontSize: 13, color: tokens.text2 },
              "& .MuiInputLabel-root.Mui-focused": { color: tokens.accent },
            }}
          >
            <InputLabel>Select Effect</InputLabel>
            <Select
              value={effect}
              label="Select Effect"
              onChange={(e) => setEffect(e.target.value)}
            >
              {EFFECTS.map((ef) => (
                <MenuItem key={ef} value={ef} sx={{ fontSize: 13 }}>
                  {ef}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </SubCard>

        {/* ── Speed slider ─────────────────────────────────────────────────── */}
        <SubCard
          icon={<SpeedIcon sx={{ fontSize: 16 }} />}
          title="Animation Speed"
          disabled={!isOn}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography sx={{ fontSize: 12, color: tokens.text2 }}>Speed</Typography>
            <Chip
              label={`${speed} ms`}
              size="small"
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 700,
                borderRadius: "20px",
                color: tokens.accent2,
                background: tokens.accentSoft,
              }}
            />
          </Stack>
          <Slider
            value={speed}
            min={1}
            max={5000}
            step={50}
            onChange={(_, v) => setSpeed(Math.max(1, Math.min(5000, Number(v))))}
            disabled={!isOn}
            sx={{
              color: tokens.accent,
              "& .MuiSlider-thumb": {
                width: 16,
                height: 16,
                background: tokens.accent,
                border: "2px solid #fff",
              },
              "& .MuiSlider-track": { height: 5, borderRadius: 3 },
              "& .MuiSlider-rail": { height: 5, borderRadius: 3, background: tokens.surface3 },
            }}
          />
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ fontSize: 10, color: tokens.text3 }}>Fast (1ms)</Typography>
            <Typography sx={{ fontSize: 10, color: tokens.text3 }}>Slow (5000ms)</Typography>
          </Stack>
        </SubCard>
      </Stack>
    </Box>
  );
};

LightController.propTypes = {
  /** Explicit robot to control. Omit to fall back to `selectedRobo` (DeliveryLayout's usage). */
  robot: PropTypes.shape({
    serialID: PropTypes.string,
    lightConfig: PropTypes.object,
  }),
};

export default LightController;
